import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaMagic, FaPaperPlane, FaPlus, FaSave, FaSyncAlt, FaTimes, FaExclamationTriangle, FaFilePdf } from "react-icons/fa";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import { useAppDialog } from "../../../components/AppDialog";
import { colors, spacing, typography, radius, shadows, inputBase, btnPrimary, btnSecondary, btnGhost } from "../../../utils/designTokens";
import { comunicacionAdmin, redactarConHabi, mensajeDeError, motivoDeError } from "../../../utils/comunicaciones/api";
import {
  MAX_ASUNTO,
  ENLACES_HABITIA,
  DESTINATARIOS,
  contenidoNuevo,
  contenidoDelServidor,
  contarMarcas,
} from "../../../utils/comunicaciones/contenido";

// "Nueva comunicación": el admin le cuenta a Habi los puntos sueltos, Habi
// arma los bloques y el admin los ajusta mirando el PDF real (lo arma el
// servidor con Chromium: lo que se ve es exactamente lo que se manda). Lo que
// Habi no sabe queda como [A COMPLETAR] y no se puede enviar hasta completarlo.
//
// Props: buildingId, edificioNombre, id (null = nueva), onVolver(), onEnviada(id), showToast

const DEMORA_VISTA_PREVIA_MS = 1500;
const AYUDA_FORMATO = 'Un renglón por línea; "- " al principio para viñetas; **así** para resaltar; [texto](https://…) para un link.';

function base64ABlobUrl(b64) {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof atob !== "function") return null;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

export default function ComunicacionEditor({ buildingId, edificioNombre, id: idInicial = null, onVolver, onEnviada, showToast }) {
  const { confirmDialog } = useAppDialog();
  const [id, setId] = useState(idInicial);
  const [version, setVersion] = useState(null);
  const [contenido, setContenido] = useState(() => contenidoNuevo(edificioNombre));
  const [destinatarios, setDestinatarios] = useState("ambos");
  const [cargando, setCargando] = useState(!!idInicial);
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [notas, setNotas] = useState("");
  const [redactando, setRedactando] = useState(false);
  const [vista, setVista] = useState(null); // { url, paginas, numeroPrevisto, problemas }
  const [armandoVista, setArmandoVista] = useState(false);
  const [errorVista, setErrorVista] = useState("");
  const [confirmar, setConfirmar] = useState(null); // respuesta de "destinatarios"
  const [enviando, setEnviando] = useState(false);
  const pedidoRef = useRef(0);
  const urlRef = useRef(null);

  // Carga del borrador.
  useEffect(() => {
    if (!idInicial) return undefined;
    let vivo = true;
    (async () => {
      try {
        const d = await comunicacionAdmin("detalle", { id: idInicial });
        if (!vivo) return;
        setContenido(contenidoDelServidor(d.contenido, edificioNombre));
        setDestinatarios(d.destinatarios || "ambos");
        setVersion(d.version || 1);
      } catch (e) {
        if (vivo) showToast && showToast(mensajeDeError(e), "error");
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [idInicial, edificioNombre, showToast]);

  useEffect(
    () => () => {
      if (urlRef.current && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  const armarVistaPrevia = useCallback(async () => {
    const pedido = ++pedidoRef.current;
    setArmandoVista(true);
    setErrorVista("");
    try {
      const r = await comunicacionAdmin("vistaPrevia", { buildingId, contenido });
      if (pedido !== pedidoRef.current) return; // llegó una más nueva
      const url = base64ABlobUrl(r.pdfBase64);
      if (urlRef.current && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setVista({ url, paginas: r.paginas, numeroPrevisto: r.numeroPrevisto, problemas: r.problemas || [] });
    } catch (e) {
      if (pedido === pedidoRef.current) setErrorVista(mensajeDeError(e));
    } finally {
      if (pedido === pedidoRef.current) setArmandoVista(false);
    }
  }, [buildingId, contenido]);

  // La vista previa se rearma sola cuando se deja de escribir.
  useEffect(() => {
    if (cargando) return undefined;
    const t = setTimeout(armarVistaPrevia, DEMORA_VISTA_PREVIA_MS);
    return () => clearTimeout(t);
  }, [armarVistaPrevia, cargando]);

  const cambiar = (patch) => {
    setContenido((c) => ({ ...c, ...patch }));
    setSucio(true);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await comunicacionAdmin("guardar", { id, buildingId, contenido, destinatarios, ...(id ? { version } : {}) });
      setId(r.id);
      setVersion(r.version);
      setSucio(false);
      return r;
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
      return null;
    } finally {
      setGuardando(false);
    }
  };

  const redactar = async () => {
    if (!notas.trim()) {
      showToast && showToast("Contale a Habi qué querés comunicar.", "warning");
      return;
    }
    if ((contenido.asunto || contenido.titulo) && !(await confirmDialog("Habi va a reemplazar el texto que ya escribiste (los enlaces y la firma quedan). ¿Seguimos?", { title: "Armar con Habi", confirmLabel: "Reemplazar" }))) {
      return;
    }
    setRedactando(true);
    try {
      const r = await redactarConHabi({ buildingId, notas, destinatarios });
      setContenido((c) => contenidoDelServidor({ ...r.contenido, enlaces: c.enlaces, firma: c.firma }, edificioNombre));
      setSucio(true);
      const faltan = contarMarcas(r.contenido);
      const aviso =
        faltan === 1
          ? "Listo. Quedó 1 dato por completar, marcado en amarillo."
          : faltan
            ? `Listo. Quedaron ${faltan} datos por completar, marcados en amarillo.`
            : "Listo: revisalo en la vista previa.";
      showToast && showToast(aviso, "success");
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
    } finally {
      setRedactando(false);
    }
  };

  const faltan = contarMarcas(contenido);
  const problemas = (vista && vista.problemas) || [];
  const sinTexto = !contenido.asunto.trim() || !contenido.titulo.trim();
  const puedeEnviar = !faltan && !sinTexto && !enviando && !guardando;

  const prepararEnvio = async () => {
    const g = sucio || !id ? await guardar() : { id, version };
    if (!g) return;
    try {
      const d = await comunicacionAdmin("destinatarios", { buildingId, destinatarios });
      setConfirmar({ ...d, id: g.id, version: g.version });
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
    }
  };

  const enviar = async () => {
    if (!confirmar) return;
    setEnviando(true);
    try {
      const r = await comunicacionAdmin("enviar", { id: confirmar.id, version: confirmar.version, confirmar: true });
      setConfirmar(null);
      showToast &&
        showToast(
          r.fueraDeHorario
            ? `Comunicación N° ${r.numero} lista: sale a partir de las 9 h (se envía de 9 a 20 h).`
            : `Comunicación N° ${r.numero} enviada.`,
          "success"
        );
      onEnviada && onEnviada(confirmar.id);
    } catch (e) {
      const motivo = motivoDeError(e);
      showToast && showToast(motivo === "version" ? "Alguien más cambió la comunicación. Recargala." : mensajeDeError(e), "error");
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) return <p style={S.hint}>Cargando la comunicación…</p>;

  const r = confirmar && confirmar.resumen;
  return (
    <div>
      <div style={S.cabecera}>
        <button type="button" style={{ ...btnGhost, padding: "8px 12px" }} className="habitia-btn-ghost" onClick={onVolver}>
          <FaArrowLeft size={12} /> Comunicaciones
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={guardar} disabled={guardando || enviando}>
          <FaSave size={13} /> {guardando ? "Guardando…" : sucio || !id ? "Guardar borrador" : "Guardado"}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary, opacity: puedeEnviar ? 1 : 0.6 }}
          className="habitia-btn-primary"
          onClick={prepararEnvio}
          disabled={!puedeEnviar}
          title={faltan ? "Completá los datos marcados en amarillo" : sinTexto ? "Falta el asunto o el título" : undefined}
        >
          <FaPaperPlane size={13} /> Enviar…
        </button>
      </div>

      <div style={S.grid}>
        <div style={S.columna}>
          <Bloque titulo="Contale a Habi" icono={<FaMagic color={colors.primary} aria-hidden="true" />}>
            <p style={S.hint}>
              Escribí los puntos sueltos: qué cambia, desde cuándo, montos, cuentas, fechas. Habi arma la comunicación y lo que no
              sepa lo deja como <span style={S.marca}>[A COMPLETAR]</span>.
            </p>
            <textarea
              aria-label="Puntos para Habi"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={5}
              style={S.textarea}
              placeholder={"- los gastos comunes se pagan a la cuenta …\n- en noviembre citamos a asamblea para …\n- Redpagos tiene un recargo de $40 + IVA que paga quien lo usa"}
              disabled={redactando}
            />
            <div>
              <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={redactar} disabled={redactando}>
                <FaMagic size={13} /> {redactando ? "Habi está escribiendo…" : "Armar con Habi"}
              </button>
            </div>
          </Bloque>

          <Bloque titulo="Encabezado">
            <FormField label="Asunto" htmlFor="com-asunto" hint={`Lo que se ve en el WhatsApp y en el email (${contenido.asunto.length}/${MAX_ASUNTO}).`}>
              <input id="com-asunto" style={inputBase} maxLength={MAX_ASUNTO} value={contenido.asunto} onChange={(e) => cambiar({ asunto: e.target.value })} />
            </FormField>
            <FormField label="Título del documento" htmlFor="com-titulo">
              <input id="com-titulo" style={inputBase} maxLength={120} value={contenido.titulo} onChange={(e) => cambiar({ titulo: e.target.value })} />
            </FormField>
            <fieldset style={S.fieldset}>
              <legend style={S.legend}>¿A quién le llega?</legend>
              <div style={S.fila}>
                {DESTINATARIOS.map((d) => (
                  <label key={d.valor} style={S.radio}>
                    <input
                      type="radio"
                      name="com-destinatarios"
                      checked={destinatarios === d.valor}
                      onChange={() => {
                        setDestinatarios(d.valor);
                        setSucio(true);
                      }}
                    />
                    {d.label}
                  </label>
                ))}
              </div>
            </fieldset>
          </Bloque>

          <Bloque titulo="Lo importante" ayuda="2 a 4 viñetas: qué cambia y qué hay que hacer. Va primero, en un recuadro.">
            {contenido.loImportante.map((x, i) => (
              <div key={i} style={S.fila}>
                <textarea
                  aria-label={`Lo importante ${i + 1}`}
                  rows={2}
                  style={{ ...S.textarea, flex: 1 }}
                  value={x}
                  onChange={(e) => cambiar({ loImportante: contenido.loImportante.map((y, k) => (k === i ? e.target.value : y)) })}
                />
                <Quitar etiqueta={`Quitar lo importante ${i + 1}`} onClick={() => cambiar({ loImportante: contenido.loImportante.filter((_, k) => k !== i) })} />
              </div>
            ))}
            <Agregar etiqueta="Agregar viñeta" onClick={() => cambiar({ loImportante: [...contenido.loImportante, ""] })} />
          </Bloque>

          <Bloque titulo="Qué tenés que hacer" ayuda="Pasos concretos para la persona, con fecha si la hay. Opcional.">
            {contenido.pasos.map((p, i) => (
              <div key={i} style={S.fila}>
                <input
                  aria-label={`Paso ${i + 1}`}
                  style={{ ...inputBase, flex: 3 }}
                  value={p.texto}
                  onChange={(e) => cambiar({ pasos: contenido.pasos.map((y, k) => (k === i ? { ...y, texto: e.target.value } : y)) })}
                />
                <input
                  aria-label={`Fecha del paso ${i + 1}`}
                  placeholder="Fecha (opcional)"
                  style={{ ...inputBase, flex: 1 }}
                  value={p.fecha}
                  onChange={(e) => cambiar({ pasos: contenido.pasos.map((y, k) => (k === i ? { ...y, fecha: e.target.value } : y)) })}
                />
                <Quitar etiqueta={`Quitar paso ${i + 1}`} onClick={() => cambiar({ pasos: contenido.pasos.filter((_, k) => k !== i) })} />
              </div>
            ))}
            <Agregar etiqueta="Agregar paso" onClick={() => cambiar({ pasos: [...contenido.pasos, { texto: "", fecha: "" }] })} />
          </Bloque>

          <Bloque titulo="Medios de pago" ayuda="Tabla con cómo pagar y el costo para la unidad. Opcional.">
            {contenido.mediosPago ? (
              <>
                {contenido.mediosPago.filas.map((f, i) => (
                  <div key={i} style={S.fila}>
                    {[
                      ["medio", "Medio", 1],
                      ["como", "Cómo", 2],
                      ["costo", "Costo", 1],
                    ].map(([campo, etiqueta, flex]) => (
                      <input
                        key={campo}
                        aria-label={`${etiqueta} ${i + 1}`}
                        placeholder={etiqueta}
                        style={{ ...inputBase, flex }}
                        value={f[campo]}
                        onChange={(e) =>
                          cambiar({ mediosPago: { ...contenido.mediosPago, filas: contenido.mediosPago.filas.map((y, k) => (k === i ? { ...y, [campo]: e.target.value } : y)) } })
                        }
                      />
                    ))}
                    <Quitar
                      etiqueta={`Quitar medio de pago ${i + 1}`}
                      onClick={() => {
                        const filas = contenido.mediosPago.filas.filter((_, k) => k !== i);
                        cambiar({ mediosPago: filas.length ? { ...contenido.mediosPago, filas } : null });
                      }}
                    />
                  </div>
                ))}
                <Agregar etiqueta="Agregar medio de pago" onClick={() => cambiar({ mediosPago: { ...contenido.mediosPago, filas: [...contenido.mediosPago.filas, { medio: "", como: "", costo: "" }] } })} />
              </>
            ) : (
              <Agregar etiqueta="Agregar tabla de medios de pago" onClick={() => cambiar({ mediosPago: { titulo: "Cómo pagar", filas: [{ medio: "", como: "", costo: "" }] } })} />
            )}
          </Bloque>

          <Bloque titulo="Secciones" ayuda={AYUDA_FORMATO}>
            {contenido.secciones.map((s, i) => (
              <div key={i} style={S.seccion}>
                <div style={S.fila}>
                  <input
                    aria-label={`Subtítulo de la sección ${i + 1}`}
                    placeholder="Subtítulo"
                    style={{ ...inputBase, flex: 1 }}
                    value={s.titulo}
                    onChange={(e) => cambiar({ secciones: contenido.secciones.map((y, k) => (k === i ? { ...y, titulo: e.target.value } : y)) })}
                  />
                  <Quitar etiqueta={`Quitar sección ${i + 1}`} onClick={() => cambiar({ secciones: contenido.secciones.filter((_, k) => k !== i) })} />
                </div>
                <textarea
                  aria-label={`Texto de la sección ${i + 1}`}
                  rows={5}
                  style={S.textarea}
                  value={s.texto}
                  onChange={(e) => cambiar({ secciones: contenido.secciones.map((y, k) => (k === i ? { ...y, texto: e.target.value } : y)) })}
                />
              </div>
            ))}
            <Agregar etiqueta="Agregar sección" onClick={() => cambiar({ secciones: [...contenido.secciones, { titulo: "", texto: "" }] })} />
          </Bloque>

          <Bloque titulo="Enlaces útiles" ayuda="Van al final, cliqueables y con la dirección escrita (sirve si lo imprimen).">
            {contenido.enlaces.map((e, i) => (
              <div key={i} style={S.fila}>
                <input
                  aria-label={`Texto del enlace ${i + 1}`}
                  style={{ ...inputBase, flex: 1 }}
                  value={e.texto}
                  onChange={(ev) => cambiar({ enlaces: contenido.enlaces.map((y, k) => (k === i ? { ...y, texto: ev.target.value } : y)) })}
                />
                <input
                  aria-label={`Dirección del enlace ${i + 1}`}
                  type="url"
                  style={{ ...inputBase, flex: 1.4 }}
                  value={e.url}
                  onChange={(ev) => cambiar({ enlaces: contenido.enlaces.map((y, k) => (k === i ? { ...y, url: ev.target.value } : y)) })}
                />
                <Quitar etiqueta={`Quitar enlace ${i + 1}`} onClick={() => cambiar({ enlaces: contenido.enlaces.filter((_, k) => k !== i) })} />
              </div>
            ))}
            <div style={S.fila}>
              <Agregar etiqueta="Agregar enlace" onClick={() => cambiar({ enlaces: [...contenido.enlaces, { texto: "", url: "https://" }] })} />
              <button type="button" style={{ ...btnGhost, padding: "6px 10px" }} className="habitia-btn-ghost" onClick={() => cambiar({ enlaces: ENLACES_HABITIA.map((x) => ({ ...x })) })}>
                Volver a los de Habitia
              </button>
            </div>
          </Bloque>

          <Bloque titulo="Firma">
            <input aria-label="Firma" style={inputBase} maxLength={120} value={contenido.firma} onChange={(e) => cambiar({ firma: e.target.value })} />
          </Bloque>
        </div>

        <div style={S.vista}>
          <div style={S.vistaCabecera}>
            <span style={S.vistaTitulo}>
              <FaFilePdf color={colors.primary} aria-hidden="true" /> Vista previa del PDF
            </span>
            <span style={S.hint}>
              {vista ? `Comunicación N° ${vista.numeroPrevisto} · ${vista.paginas} ${vista.paginas === 1 ? "página" : "páginas"}` : ""}
            </span>
            <button type="button" style={{ ...btnGhost, padding: "6px 10px" }} className="habitia-btn-ghost" onClick={armarVistaPrevia} disabled={armandoVista} aria-label="Actualizar la vista previa">
              <FaSyncAlt size={12} /> {armandoVista ? "Armando…" : "Actualizar"}
            </button>
          </div>
          {faltan > 0 && (
            <div style={S.aviso} role="status">
              <FaExclamationTriangle color={colors.warningDark} aria-hidden="true" />
              {faltan === 1 ? "Falta 1 dato" : `Faltan ${faltan} datos`} marcados en amarillo. Completalos para poder enviar.
            </div>
          )}
          {vista && vista.paginas > 2 && (
            <div style={S.aviso} role="status">
              <FaExclamationTriangle color={colors.warningDark} aria-hidden="true" /> Tiene {vista.paginas} páginas: lo ideal es 1 o 2 (si es largo, se lee menos).
            </div>
          )}
          {problemas.length > 0 && !faltan && <p style={S.hint}>{problemas[0]}</p>}
          {errorVista && (
            <p style={S.error} role="alert">
              No se pudo armar la vista previa: {errorVista}
            </p>
          )}
          {vista && vista.url ? (
            <iframe title="Vista previa del PDF" src={`${vista.url}#toolbar=0&view=FitH`} style={S.iframe} />
          ) : (
            <div style={S.vacio}>{armandoVista ? "Armando el PDF…" : "La vista previa aparece cuando empezás a escribir."}</div>
          )}
        </div>
      </div>

      <AdminModal
        open={!!confirmar}
        onClose={enviando ? () => {} : () => setConfirmar(null)}
        title="Enviar la comunicación"
        subtitle={contenido.asunto}
        size="medium"
        icon={<FaPaperPlane />}
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setConfirmar(null)} disabled={enviando}>
              Cancelar
            </button>
            <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={enviar} disabled={enviando || !r || !r.total}>
              <FaPaperPlane size={13} /> {enviando ? "Enviando…" : r ? `Enviar a ${r.total} ${r.total === 1 ? "persona" : "personas"}` : "Enviar"}
            </button>
          </div>
        }
      >
        {r && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
            <ul style={S.lista}>
              <li>
                Va a <strong style={S.fuerte}>{r.total}</strong> {r.total === 1 ? "persona" : "personas"} ({(DESTINATARIOS.find((d) => d.valor === destinatarios) || DESTINATARIOS[0]).label.toLowerCase()}).
              </li>
              <li>WhatsApp con el PDF: {r.conWhatsapp}. Costo estimado: US$ {r.costoWhatsappUsd.toFixed(2)}.</li>
              <li>Email con el PDF adjunto: {r.conEmail}.</li>
              {r.sinContacto > 0 && <li>Sin celular ni email: {r.sinContacto} (la ven en la app de Habitia).</li>}
            </ul>
            {!confirmar.plantillaAprobada && (
              <div style={S.aviso} role="status">
                <FaExclamationTriangle color={colors.warningDark} aria-hidden="true" /> Meta todavía no aprobó la plantilla de WhatsApp: por ahora sale solo el
                email. Cuando se apruebe, el WhatsApp se reenvía desde el seguimiento.
              </div>
            )}
            {confirmar.dryRun && (
              <div style={S.aviso} role="status">
                <FaExclamationTriangle color={colors.warningDark} aria-hidden="true" /> Modo prueba: solo les llega a los contactos de prueba.
              </div>
            )}
            <p style={S.hint}>Se envía de 9 a 20 h; fuera de ese horario queda en cola. Una vez enviada no se puede editar.</p>
          </div>
        )}
      </AdminModal>
    </div>
  );
}

function Bloque({ titulo, ayuda, icono, children }) {
  return (
    <section style={S.bloque}>
      <h3 style={S.bloqueTitulo}>
        {icono} {titulo}
      </h3>
      {ayuda && <p style={{ ...S.hint, marginTop: -4 }}>{ayuda}</p>}
      {children}
    </section>
  );
}

function Agregar({ etiqueta, onClick }) {
  return (
    <button type="button" style={{ ...btnGhost, padding: "6px 10px", alignSelf: "flex-start" }} className="habitia-btn-ghost" onClick={onClick}>
      <FaPlus size={11} /> {etiqueta}
    </button>
  );
}

function Quitar({ etiqueta, onClick }) {
  return (
    <button type="button" style={S.quitar} onClick={onClick} aria-label={etiqueta} title={etiqueta}>
      <FaTimes size={12} />
    </button>
  );
}

const S = {
  cabecera: { display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: spacing[5], alignItems: "start" },
  columna: { display: "flex", flexDirection: "column", gap: spacing[4], minWidth: 0 },
  bloque: { background: colors.white, borderRadius: radius.md, boxShadow: shadows.sm, padding: spacing[4], display: "flex", flexDirection: "column", gap: spacing[3] },
  bloqueTitulo: { margin: 0, fontSize: typography.sizes.md, fontWeight: 400, color: colors.primaryDark, display: "flex", alignItems: "center", gap: spacing[2] },
  hint: { margin: 0, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 },
  marca: { background: colors.warningLight, color: colors.warningDark, padding: "0 3px", borderRadius: 3 },
  textarea: { ...inputBase, resize: "vertical", lineHeight: 1.5 },
  fila: { display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap" },
  seccion: { display: "flex", flexDirection: "column", gap: spacing[2], paddingBottom: spacing[3], borderBottom: `1px solid ${colors.gray100}` },
  fieldset: { border: "none", margin: 0, padding: 0 },
  legend: { fontSize: typography.sizes.base, color: colors.gray900, marginBottom: spacing[1] },
  radio: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: typography.sizes.sm, color: colors.gray900, marginRight: spacing[3], cursor: "pointer" },
  quitar: { border: "none", background: "transparent", color: colors.gray400, cursor: "pointer", padding: 6, borderRadius: radius.sm },
  vista: { position: "sticky", top: spacing[4], background: colors.white, borderRadius: radius.md, boxShadow: shadows.sm, padding: spacing[4], display: "flex", flexDirection: "column", gap: spacing[2], minWidth: 0 },
  vistaCabecera: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  vistaTitulo: { display: "inline-flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.md, color: colors.gray900, flex: 1 },
  iframe: { width: "100%", height: "calc(100vh - 260px)", minHeight: 520, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.gray50 },
  vacio: { height: 420, display: "flex", alignItems: "center", justifyContent: "center", color: colors.gray400, fontSize: typography.sizes.sm, border: `1px dashed ${colors.gray200}`, borderRadius: radius.sm },
  aviso: { display: "flex", alignItems: "flex-start", gap: spacing[2], background: colors.warningLight, color: colors.gray900, padding: `${spacing[2]}px ${spacing[3]}px`, borderRadius: radius.sm, fontSize: typography.sizes.sm, lineHeight: 1.5 },
  error: { margin: 0, color: colors.errorDark, fontSize: typography.sizes.sm },
  lista: { margin: 0, paddingLeft: spacing[5], display: "flex", flexDirection: "column", gap: spacing[1], fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.5 },
  fuerte: { fontWeight: 400, color: colors.primaryDark },
};
